import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import {
  Mail, Shield, CheckCircle2, RefreshCw, ChevronDown, ChevronUp,
  Clock, FileText, UserCheck, AlertCircle, Replace,
} from "lucide-react";

/**
 * WP-I — Persistent email audit trail for the matter accordion (LV-13).
 *
 * Renders the full chronological audit log for a matter — email events,
 * OB link sent / returned, CCL sent, conflict decisions, reference
 * corrections.
 *
 * Contract: NO conditional filter on pipeline stage / ob_status.  The
 * audit trail is append-only so a regulator question like "when was
 * the OB link sent to X?" is always answerable regardless of how far
 * the matter has progressed.  Tests in
 * ``backend/tests/test_wp_i_audit_trail.py`` assert this.
 */

const EVENT_ICONS = {
  ob_link_sent:              Mail,
  email_sent:                Mail,
  email_opened:              Mail,
  email_event:               Mail,
  ob_form_returned:          UserCheck,
  ccl_sent:                  FileText,
  ccl_generated:             FileText,
  conflict_check_submitted:  Shield,
  conflict_decision:         Shield,
  matter_reference_renamed:  Replace,
  matter_audit:              Replace,
  forf_section_1_submitted:  FileText,
  forf_section_2_submitted:  FileText,
  default:                   Clock,
};

const EVENT_COLOURS = {
  ob_link_sent:              "text-blue-600 bg-blue-50 border-blue-200",
  email_sent:                "text-blue-600 bg-blue-50 border-blue-200",
  email_opened:              "text-blue-700 bg-blue-100 border-blue-200",
  ob_form_returned:          "text-teal bg-teal-dim/20 border-teal/30",
  ccl_sent:                  "text-teal bg-teal-dim/20 border-teal/30",
  ccl_generated:             "text-teal bg-teal-dim/20 border-teal/30",
  conflict_check_submitted:  "text-purple-600 bg-purple-50 border-purple-200",
  conflict_decision:         "text-purple-700 bg-purple-50 border-purple-200",
  matter_reference_renamed:  "text-status-amber bg-status-amber-dim border-amber-200",
  matter_audit:              "text-status-amber bg-status-amber-dim border-amber-200",
  default:                   "text-slate-500 bg-slate-50 border-slate-200",
};

function formatTimestamp(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(ts).slice(0, 16);
  }
}

function getUserId() {
  return (
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    ""
  );
}

export default function MatterAuditAccordion({ matterReference, defaultOpen = true }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(Boolean(defaultOpen));

  useEffect(() => {
    if (!matterReference) return;
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterReference]);

  const fetchEvents = async () => {
    setLoading(true);
    setErr("");
    try {
      const resp = await axios.get(
        `${API}/hub/matter-audit/${encodeURIComponent(matterReference)}`,
        { headers: { "X-Bonsai-User-Id": getUserId() } },
      );
      setEvents(resp.data?.events || []);
    } catch (e) {
      setErr(errorMessage(e, "Failed to load audit trail"));
    } finally {
      setLoading(false);
    }
  };

  if (!matterReference) return null;

  return (
    <div
      className="rounded-md border border-navy-border bg-navy-card overflow-hidden"
      data-testid={`matter-audit-accordion-${matterReference}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2 bg-navy-hover/60 text-left"
        data-testid={`matter-audit-toggle-${matterReference}`}
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-teal uppercase tracking-wider">
          <Clock className="w-3.5 h-3.5" /> Audit trail
          <span className="text-txt-muted font-normal normal-case tracking-normal">
            ({events.length} event{events.length === 1 ? "" : "s"})
          </span>
        </span>
        <span className="flex items-center gap-2">
          <RefreshCw
            className={`w-3.5 h-3.5 text-txt-muted ${loading ? "animate-spin" : ""}`}
            onClick={(e) => { e.stopPropagation(); fetchEvents(); }}
            data-testid={`matter-audit-refresh-${matterReference}`}
          />
          {open
            ? <ChevronUp   className="w-4 h-4 text-txt-muted" />
            : <ChevronDown className="w-4 h-4 text-txt-muted" />}
        </span>
      </button>

      {open && (
        <div className="px-4 py-3" data-testid={`matter-audit-body-${matterReference}`}>
          {err && (
            <div className="flex items-center gap-2 p-2 rounded border border-red-200 bg-red-50 text-xs text-red-700" data-testid={`matter-audit-error-${matterReference}`}>
              <AlertCircle className="w-3.5 h-3.5" />
              <span>{err}</span>
            </div>
          )}

          {!err && events.length === 0 && !loading && (
            <p className="text-xs text-txt-muted italic" data-testid={`matter-audit-empty-${matterReference}`}>
              No audit events recorded yet.
            </p>
          )}

          <ul className="space-y-2" data-testid={`matter-audit-events-${matterReference}`}>
            {events.map((ev, i) => {
              const Icon = EVENT_ICONS[ev.event] || EVENT_ICONS.default;
              const colour = EVENT_COLOURS[ev.event] || EVENT_COLOURS.default;
              return (
                <li
                  key={`${ev.event}-${ev.timestamp}-${i}`}
                  className={`flex items-start gap-3 p-2 rounded border ${colour}`}
                  data-testid={`matter-audit-event-${ev.event}-${i}`}
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0 text-xs">
                    <p className="font-semibold text-slate-700">
                      {ev.description || ev.event}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-slate-500">
                      <span>{formatTimestamp(ev.timestamp)}</span>
                      {ev.metadata?.recipient && (
                        <span data-testid={`matter-audit-recipient-${i}`}>
                          to {ev.metadata.recipient}
                        </span>
                      )}
                      {ev.metadata?.delivery && (
                        <span className="inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-teal" />
                          {ev.metadata.delivery}
                        </span>
                      )}
                      {ev.source && (
                        <span className="text-[10px] text-slate-400 font-mono">
                          [{ev.source}]
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
